/**
 * Adds safety wrappers around editor methods that are known to throw when
 * table selection state is temporarily out of sync with the DOM.
 *
 * Addresses three uncaught errors observed during table interactions:
 *
 * 1. `Cannot read properties of null (reading 'setAttribute')` in setFragmentData —
 *    handled in withSetFragmentDataTable.js directly.
 *
 * 2. `IndexSizeError: Failed to execute 'setEnd' on 'Range'` in toSlatePoint —
 *    fired by the browser's selectionchange handler when the DOM child-count has
 *    changed since the last render (e.g. clicking outside a table cell). Caught
 *    here on `apply` so it doesn't surface as an uncaught exception.
 *
 * 3. `Cannot get the leaf node at path [N,M]` in getMarks — occurs when the
 *    selection focus temporarily points at a block element instead of a text
 *    leaf (possible after programmatic selection restoration). Caught here and
 *    returns null so toolbar mark-state reads gracefully degrade.
 */
export const withSafeTable = (editor) => {
  const { getMarks, apply } = editor;

  // Guard getMarks against "non-leaf node" errors when the selection path
  // points to a block element rather than a text leaf.
  editor.getMarks = () => {
    try {
      return getMarks();
    } catch (e) {
      if (e && e.message && e.message.includes('non-leaf node')) {
        return null;
      }
      throw e;
    }
  };

  // Guard apply against IndexSizeError DOM exceptions that fire when Slate
  // tries to reconcile a stale DOM selection offset after a table operation.
  editor.apply = (operation) => {
    try {
      apply(operation);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'IndexSizeError') {
        return;
      }
      throw e;
    }
  };

  return editor;
};
